import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createItem, updateItem } from "../../services/InventorySettings";
import { IItemRequestDTO } from "../../interfaces/InventorySettings";
import { formFieldStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditItemProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  categoryId: number;
  typeId: number;
  onSuccess: (message: string) => void;
}

const AddEditItem: React.FC<AddEditItemProps> = ({
  open,
  onClose,
  editData,
  categoryId,
  typeId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    itemName: "",
    weight: "",
    cost: "",
    sellingPrice: "",
    stockAlert: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const COMPANY_ID = loggedInUser?.companyId;

  // Debug logging
  console.log("AddEditItem props:", { categoryId, typeId, editData, COMPANY_ID });

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          itemName: editData.itemName || "",
          weight: editData.weight?.toString() || "",
          cost: editData.cost?.toString() || "",
          sellingPrice: editData.sellingPrice?.toString() || "",
          stockAlert: editData.stockAlert?.toString() || "",
        });
      } else {
        setFormData({
          itemName: "",
          weight: "",
          cost: "",
          sellingPrice: "",
          stockAlert: "",
        });
      }
      setErrors({});
    }
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.itemName) newErrors.itemName = "Item name is required";
    if (!formData.weight || Number(formData.weight) <= 0) newErrors.weight = "Weight is required";
    if (!formData.cost || Number(formData.cost) <= 0) newErrors.cost = "Cost is required";
    if (!formData.sellingPrice || Number(formData.sellingPrice) <= 0) newErrors.sellingPrice = "Selling price is required";
    if (!formData.stockAlert || Number(formData.stockAlert) < 0) newErrors.stockAlert = "Stock alert is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }
    if (!categoryId || !typeId) {
      setSnackbarMessage("Category and Type information is required");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IItemRequestDTO = {
        companyId: COMPANY_ID,
        itemName: formData.itemName,
        categoryId: categoryId,
        subcategoryId: categoryId, // Try sending categoryId as subcategoryId too
        typeId: typeId,
        weight: parseFloat(formData.weight),
        cost: parseFloat(formData.cost),
        sellingPrice: parseFloat(formData.sellingPrice),
        stockAlert: parseInt(formData.stockAlert),
      };

      console.log("Sending DTO:", dto); // Debug log
      console.log("DTO JSON:", JSON.stringify(dto)); // Debug log

      const response = isEdit 
        ? await updateItem(editData.id, dto)
        : await createItem(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Item updated successfully" : "Item created successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || response?.errorResponse?.message || "Operation failed";
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err?.message || "An error occurred while saving");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5, pt: 1.5, px: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>{editData ? "Edit Item" : "Add Item"}</Typography>
          <Typography variant="body2" color="grey.500" sx={{ fontSize: "12px", mt: 0.3 }}>
            {editData ? "Edit Item" : "Add Item"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Item Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. iPhone 15 Pro"
              value={formData.itemName}
              onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
              error={!!errors.itemName}
              helperText={errors.itemName}
              sx={{...formFieldStyles}}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Cost <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="999.99"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              error={!!errors.cost}
              helperText={errors.cost}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Selling Price <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1299.99"
              value={formData.sellingPrice}
              onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })}
              error={!!errors.sellingPrice}
              helperText={errors.sellingPrice}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Item Weight <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="5kg"
              value={formData.weight}
              onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
              error={!!errors.weight}
              helperText={errors.weight}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Stock Alert <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="10"
              value={formData.stockAlert}
              onChange={(e) => setFormData({ ...formData, stockAlert: e.target.value })}
              error={!!errors.stockAlert}
              helperText={errors.stockAlert}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>{editData ? "Save" : "Add"}</Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditItem;